/** @jsxImportSource @emotion/react */
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Tooltip} from '@mayo/mayo-ui-gakki';
import {Wrapper, Dot, PlusIcon} from './components';
import {useCompanyContext} from '@gakki/context/company-context';
import {useChartDataContext} from '../../context/ChartDataContext';
import {DeleteDialog} from '@gakki/components/Dialog';
import {ReactComponent as ActiveUser} from '@gakki/assets/orgchart-active-user.svg';
import {ReactComponent as User} from '@gakki/assets/orgchart-user.svg';
import cx from 'classnames';
import {ReactComponent as TrashIcon} from '@common/assets/icon_trash.svg';

const propTypes = {
  nodeData: PropTypes.object.isRequired,
};

const CustomNode = props => {
  const {
    removeNode,
    onFormShow,
    addNode,
    addSiblingNodes,
    dataSource,
    isConfirmed,
  } = useChartDataContext();
  const {employee} = useCompanyContext();
  const [isDelete, setIsDelete] = useState(false);
  const {nodeData} = props;
  const hasChildren = nodeData.children && nodeData.children.length !== 0;
  const onlyOneRootNode = dataSource.children?.length === 1;
  const isRootNode = nodeData.identity === 'root';

  const supervisors =
    nodeData.supervisors
      ?.filter(s => s.jobNo)
      .map(s => {
        const supervisorData = employee.find(e => e.jobNo === s.jobNo);
        if (supervisorData) {
          return {...s, ...supervisorData};
        }
        return s;
      }) || [];
  const isModified = !!nodeData.departmentName;
  const isError = isConfirmed && nodeData.error;

  const circleStyle = {
    transform: 'translate(-50%, 72px)',
    border: '2px solid #3e4348',
    borderRadius: '18px',
  };

  const onEdit = () => {
    onFormShow({formStatus: 'EDIT', nodeData, isRootNode});
  };
  const onDelete = () => {
    removeNode(nodeData.id);
  };

  const onAdd = () => {
    addNode(nodeData);
  };

  const onAddSibling = () => {
    addSiblingNodes(nodeData);
  };

  return (
    <Wrapper className={isError ? '!bg-[#F3DBDA]' : ''}>
      {!isRootNode && <Dot className={'-translate-x-1/2 -translate-y-1/2'} />}
      <div
        className={cx(
          'absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full w-[54px] h-[54px] flex justify-center items-center',
          {
            'bg-white': !isError,
            'bg-[#F3DBDA]': isError,
          },
        )}
      >
        <div className="bg-white rounded-full overflow-hidden w-[44px] h-[44px]">
          {nodeData.supervisors?.[0]?.jobNo ? <ActiveUser /> : <User />}
        </div>
      </div>
      <Tooltip
        content="點選此方塊編輯部門資料"
        fullWidth
        fullHeight
        placement="bottom"
      >
        <div
          onClick={onEdit}
          className={
            'w-full cursor-pointer text-[#3e4348] text-base text-center info-node'
          }
        >
          {isModified ? (
            <div>
              <div className="text-primary mb-[10px]">{`${nodeData.departmentName} (${nodeData.employees.length})`}</div>
              <div className="leading-[25px]">
                {supervisors.length > 0
                  ? supervisors.map((s, idx) => {
                      const isMainSupervisor = s.supervisorStatus === 0;
                      return (
                        <span
                          className={cx('whitespace-nowrap', {
                            'text-[#fb782d]': isMainSupervisor,
                          })}
                        >
                          {`${s.employeeNumber}/${s.name}`}
                          <span className="!text-[#3e4348] mx-[5px]">
                            {idx !== supervisors.length - 1 ? '｜' : ''}
                          </span>
                        </span>
                      );
                    })
                  : '尚無部門主管'}
              </div>
            </div>
          ) : (
            '編輯部門'
          )}
        </div>
      </Tooltip>
      {isRootNode && onlyOneRootNode ? null : (
        <div className="absolute top-[10px] right-[10px]">
          <Tooltip isIcon content="刪除">
            <div>
              <Button
                variant={'simple'}
                colorSchema={'primary'}
                onClick={() => setIsDelete(true)}
                aria-label="delete-org"
                icon={<TrashIcon />}
              />
            </div>
          </Tooltip>
        </div>
      )}
      {hasChildren && (
        <Dot
          className={'bottom-0 !top-auto -translate-x-1/2 translate-y-1/2'}
        />
      )}
      <DeleteDialog
        text={
          <span>
            刪除後，
            <br />
            此部門同其全部轄下部門都將一起刪除，
            <br />
            請問您依舊要刪除嗎？
          </span>
        }
        isOpen={isDelete}
        onCancel={() => setIsDelete(false)}
        onConfirm={onDelete}
      />
      <div
        className={cx(
          'flex text-3xl text-[#3e4348] absolute bottom-0 left-1/2 z-[1] cursor-pointer',
          '-translate-x-1/2 translate-y-1/2',
          'node-add-btn',
        )}
        style={hasChildren ? circleStyle : {}}
        onClick={onAdd}
        aria-label="add-org"
      >
        <Tooltip
          alignmentAxis={-24}
          content={
            <div>
              點選 ＋ 新增下層部門
              <br />
              若要建立下層部門的的平行部門，請再次點選 ＋
            </div>
          }
        >
          <PlusIcon />
        </Tooltip>
      </div>
      {isRootNode && (
        <div
          className={cx(
            'flex text-3xl text-[#3e4348] absolute top-1/2 right-0 z-[1] cursor-pointer',
            'translate-x-1/2 -translate-y-1/2',
            'node-add-btn',
          )}
          onClick={onAddSibling}
          aria-label="add-org"
        >
          <Tooltip
            alignmentAxis={-24}
            content={<div>新增平行點選 ＋ 新增平行部門</div>}
          >
            <PlusIcon />
          </Tooltip>
        </div>
      )}
    </Wrapper>
  );
};

CustomNode.propTypes = propTypes;

export default CustomNode;
